import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";

export const Container = ({ children, className = "" }) => (
  <div className={`max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 ${className}`}>{children}</div>
);

export const Honeypot = ({ value, onChange }) => (
  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
    <input type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={onChange} />
  </div>
);

export const WhatsappIcon = ({ size = 16, className = "" }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 005.71 1.447h.006c6.585 0 11.946-5.335 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411" />
  </svg>
);

export const ExportBadge = ({ className = "" }) => (
  <div className={`inline-flex items-center gap-3.5 border border-line bg-white/60 px-5 py-3.5 ${className}`} data-testid="export-badge">
    <ShieldCheck size={30} strokeWidth={1.4} className="text-brand shrink-0" />
    <div className="text-left">
      <p className="text-[13.5px] font-semibold text-ink leading-tight">İhracata Uygun Ürün Garantisi</p>
      <p className="text-[11.5px] text-mute leading-snug mt-1">Uluslararası kalite ve uyumluluk standartlarında üretim</p>
    </div>
  </div>
);

export const Eyebrow = ({ children, className = "", ...rest }) => (
  <p className={`text-[11px] font-semibold tracking-[0.28em] uppercase text-brand ${className}`} {...rest}>
    {children}
  </p>
);

const variants = {
  primary: "bg-brand text-white hover:bg-brandDark hover:shadow-[0_14px_34px_rgba(217,38,28,0.28)]",
  dark: "bg-ink text-white hover:bg-brand",
  ghost: "border border-ink/20 text-ink hover:border-ink hover:bg-ink hover:text-white",
  light: "bg-white text-ink border border-line hover:border-ink",
};

export const Cta = ({ to, href, children, variant = "primary", arrow = true, className = "", ...rest }) => {
  const cls = `group inline-flex items-center justify-center gap-2.5 h-12 px-7 rounded-full text-[13px] font-semibold tracking-[0.04em] transition-all duration-300 ${variants[variant]} ${className}`;
  const inner = (
    <>
      {children}
      {arrow && <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />}
    </>
  );
  if (href) {
    return (
      <a href={href} className={cls} {...rest}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} {...rest}>
      {inner}
    </Link>
  );
};

export const TextLink = ({ to, children, className = "", ...rest }) => (
  <Link
    to={to}
    className={`group inline-flex items-center gap-2 text-[13px] font-semibold text-ink hover:text-brand transition-colors ${className}`}
    {...rest}
  >
    <span className="border-b border-ink/25 group-hover:border-brand transition-colors pb-0.5">{children}</span>
    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export const SectionHead = ({ eyebrow, title, text, action, align = "left", className = "" }) => (
  <div
    className={`flex flex-col gap-6 md:flex-row md:items-end md:justify-between ${
      align === "center" ? "md:flex-col md:items-center text-center" : ""
    } ${className}`}
  >
    <div className={align === "center" ? "max-w-2xl" : "max-w-2xl"}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="font-display font-light text-[2rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] text-ink mt-4">{title}</h2>
      {text && <p className="text-ink2 leading-relaxed mt-5 text-[15px] sm:text-base">{text}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

export const PageHeader = ({ eyebrow, title, text, children }) => (
  <section className="border-b border-line bg-canvas">
    <Container className="pt-14 pb-12 sm:pt-20 sm:pb-16 grid lg:grid-cols-12 gap-10 items-end">
      <div className="lg:col-span-7">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-[-0.015em] text-ink mt-5">{title}</h1>
        {text && <p className="text-ink2 leading-relaxed mt-6 max-w-xl text-[15px] sm:text-base">{text}</p>}
      </div>
      {children && <div className="lg:col-span-5">{children}</div>}
    </Container>
  </section>
);
